import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TreatmentRecords.css';
import { API_BASE_URL } from '../config';

// const API_BASE_URL = 'http://localhost:3001';

function TreatmentRecords() {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [searchFarm, setSearchFarm] = useState('');
  const [searchCowNumber, setSearchCowNumber] = useState('');
  const [searchLifeNumber, setSearchLifeNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch all treatments (we'll filter on frontend for now)
  const fetchTreatments = async () => {
    try {
      setLoading(true);
      setError(null);

      // For now, we'll need to get treatments from all herds
      // In production, you might want a dedicated endpoint
      const response = await axios.get(`${API_BASE_URL}/treatments/all`, {
        withCredentials: true
      });

      setTreatments(response.data.treatments || []);
    } catch (err) {
      console.error('Error fetching treatments:', err);
      setError(err.response?.data?.message || 'Failed to fetch treatment records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTreatments();
  }, []);

  // Filter treatments based on search criteria
  const filteredTreatments = treatments.filter(treatment => {
    const matchesFarm = !searchFarm ||
      treatment.farm_name?.toLowerCase().includes(searchFarm.toLowerCase()) ||
      treatment.herd_id?.toLowerCase().includes(searchFarm.toLowerCase());

    const matchesCowNumber = !searchCowNumber ||
      treatment.cow_number?.toString().includes(searchCowNumber);

    const matchesLifeNumber = !searchLifeNumber ||
      treatment.life_number?.toString().includes(searchLifeNumber);

    const matchesDateRange = (!startDate || new Date(treatment.treatment_date) >= new Date(startDate)) &&
                            (!endDate || new Date(treatment.treatment_date) <= new Date(endDate));

    return matchesFarm && matchesCowNumber && matchesLifeNumber && matchesDateRange;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTreatments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTreatments = filteredTreatments.slice(startIndex, startIndex + itemsPerPage);

  const handleClearFilters = () => {
    setSearchFarm('');
    setSearchCowNumber('');
    setSearchLifeNumber('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getProductsUsed = (products) => {
    if (!products) return 'None';
    const usedProducts = Object.entries(products)
      .filter(([_, isMarked]) => isMarked)
      .map(([name, _]) => name);
    return usedProducts.length > 0 ? usedProducts.join(', ') : 'None';
  };

  return (
    <div className="treatment-records">
      <div className="page-header">
        <h1>Treatment Records</h1>
        <p className="subtitle">View and search all saved treatment records</p>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h2>Search Filters</h2>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Farm/Herd ID</label>
            <input
              type="text"
              placeholder="Search by farm name or herd ID..."
              value={searchFarm}
              onChange={(e) => setSearchFarm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Cow Number</label>
            <input
              type="text"
              placeholder="Search by cow number..."
              value={searchCowNumber}
              onChange={(e) => setSearchCowNumber(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Life Number</label>
            <input
              type="text"
              placeholder="Search by life number..."
              value={searchLifeNumber}
              onChange={(e) => setSearchLifeNumber(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <button className="btn-clear" onClick={handleClearFilters}>
              Clear Filters
            </button>
            <button className="btn-refresh" onClick={fetchTreatments}>
              Refresh
            </button>
          </div>
        </div>

        <div className="results-summary">
          Showing {paginatedTreatments.length} of {filteredTreatments.length} records
          {treatments.length !== filteredTreatments.length && ` (filtered from ${treatments.length} total)`}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading treatment records...</p>
        </div>
      )}

      {/* Treatments Table */}
      {!loading && !error && (
        <>
          <div className="treatments-table-container">
            <table className="treatments-table">
              <thead>
                <tr>
                  <th>Treatment Date</th>
                  <th>Farm Name</th>
                  <th>Herd ID</th>
                  <th>Cow Number</th>
                  <th>Life Number</th>
                  <th>Calving Date</th>
                  <th>Products Used</th>
                  <th>Observations</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTreatments.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data">
                      {filteredTreatments.length === 0 && treatments.length > 0
                        ? 'No treatments match your search criteria'
                        : 'No treatment records found'}
                    </td>
                  </tr>
                ) : (
                  paginatedTreatments.map((treatment) => (
                    <tr key={treatment.id}>
                      <td>{treatment.treatment_date || 'N/A'}</td>
                      <td>{treatment.farm_name || 'N/A'}</td>
                      <td>{treatment.herd_id || 'N/A'}</td>
                      <td>{treatment.cow_number || 'N/A'}</td>
                      <td>{treatment.life_number || 'N/A'}</td>
                      <td>{formatDate(treatment.calving_date)}</td>
                      <td className="products-cell">
                        {getProductsUsed(treatment.products)}
                      </td>
                      <td className="observations-cell">
                        {treatment.observations || '-'}
                      </td>
                      <td>{formatDate(treatment.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TreatmentRecords;
